import { LoadingState } from 'ext-ui/hooks/useLoadingStateAnimation'
import React, { useEffect, useState } from 'react'
import { useDebounced } from 'src/hooks'
import { useNormalizeBackendError } from 'src/utils/error.utils'

export interface UseAsyncTextInputOptions {
  /**
   * The current value from external state (controlled value)
   */
  value?: string
  /**
   * callback that handles the async operation when the input value changes
   */
  onValueChange: (value: string) => Promise<void> | void
  /**
   * Debounce delay in milliseconds
   * @default 500
   */
  debounceDelay?: number
}

export interface UseAsyncTextInputReturn {
  /**
   * local value to reflect user changes
   */
  value: string
  loadingState: LoadingState
  errorText: string | undefined
  onChange: (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>,
  ) => void
}

/**
 * Hook that manages async text input with debouncing, loading states, and error handling.
 * Abstracts the common pattern of local state + debounced async operations.
 */
export const useAsyncTextInput = ({
  value = '',
  onValueChange,
  debounceDelay = 500,
}: UseAsyncTextInputOptions): UseAsyncTextInputReturn => {
  const normalizeError = useNormalizeBackendError()

  // Local state for immediate UI updates
  const [localValue, setLocalValue] = useState<string>(value)
  const [errorText, setErrorText] = useState<string | undefined>()
  const [loadingState, setLoadingState] = useState<LoadingState>('idle')

  // Handle async value change with error handling
  const handleAsyncValueChange = async (newValue: string) => {
    const result = onValueChange(newValue)

    // Check if onValueChange returns a Promise
    if (
      result &&
      typeof result === 'object' &&
      typeof result.then === 'function'
    ) {
      setLoadingState('pending')

      // TODO: consider abstracting away disabled behaviour in the hook as well
      await result
        .then(() => {
          setLoadingState('success')
        })
        .catch((error) => {
          const e = normalizeError(error)
          setLoadingState('error')
          setErrorText(e?.error)
          // Reset local value to the last known good value
          setLocalValue(value)
        })
    }
  }

  // Debounce the async handler
  const { debouncedF: debouncedHandleAsyncValueChange } = useDebounced(
    handleAsyncValueChange,
    debounceDelay,
  )

  // Handle input change
  const handleInputChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>,
  ) => {
    const newValue = e.target.value

    // Clear any previous errors
    setErrorText(undefined)
    setLoadingState('idle')

    // Update local state immediately for responsive UI
    setLocalValue(newValue)

    // Debounce the async operation
    debouncedHandleAsyncValueChange(newValue)
  }

  // Sync local value with external value changes
  useEffect(() => {
    setLocalValue(value)
  }, [value])

  return {
    value: localValue,
    loadingState,
    errorText,
    onChange: handleInputChange,
  }
}
